import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { veiculosApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Card, CardBody, CardHeader, ProgressBar, type Tone } from '../../components/ui/Card'
import { ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { PageHeader } from '../../layout/PageHeader'
import { TIPO_CUSTO_VEICULO_LABEL, TipoAlertaVeiculo } from '../../lib/enums'
import { cn, formatDate, formatMoney } from '../../lib/utils'
import { BloqueioRecurso } from '../plano/BloqueioRecurso'
import { useRecurso } from '../plano/useRecurso'
import { FiltroPeriodo } from './FiltroPeriodo'
import { formatKm, mesReferencia, numeroBr } from './veiculoUtils'

const ALERTA: Record<TipoAlertaVeiculo, { icone: string; tom: string; tone: Tone }> = {
  [TipoAlertaVeiculo.ConsumoPior]: { icone: 'ri-speed-line', tom: 'bg-warning/10 text-warning', tone: 'warning' },
  [TipoAlertaVeiculo.KmRegredindo]: { icone: 'ri-arrow-go-back-line', tom: 'bg-expense/10 text-expense', tone: 'expense' },
  [TipoAlertaVeiculo.AbastecimentoDuplicado]: { icone: 'ri-file-copy-line', tom: 'bg-info/10 text-info', tone: 'info' },
  [TipoAlertaVeiculo.PrecoAltoCombustivel]: { icone: 'ri-price-tag-3-line', tom: 'bg-warning/10 text-warning', tone: 'warning' },
}

const porKm = (n?: number | null) => (n != null ? `${formatMoney(n)}/km` : '—')

export function InsightsVeiculoPage() {
  const { id = '' } = useParams()
  const modulo = useRecurso('veiculos.modulo')
  const insightsOk = useRecurso('veiculos.insights')
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')

  const { data: v, isLoading: carregandoV, isError: erroV, error } = useQuery({
    queryKey: ['veiculos', id], queryFn: () => veiculosApi.obter(id), enabled: !!id && modulo.habilitado,
  })
  const { data, isLoading, isError } = useQuery({
    queryKey: ['veiculos', id, 'insights', de, ate],
    queryFn: () => veiculosApi.insights(id, { de: de || undefined, ate: ate || undefined }),
    enabled: !!id && modulo.habilitado && insightsOk.habilitado,
  })

  const titulo = 'Insights do veículo'
  if (!modulo.habilitado) return <><PageHeader title={titulo} /><BloqueioRecurso mensagem="O módulo de veículos não está incluído no seu plano." /></>
  if (!insightsOk.habilitado) {
    return <><PageHeader title={titulo} /><BloqueioRecurso mensagem="Os insights de custo total, depreciação e projeção não estão incluídos no seu plano. O básico continua disponível em Consumo." /></>
  }
  if (carregandoV) return <><PageHeader title={titulo} /><Card><LoadingState /></Card></>
  if (erroV || !v) return <><PageHeader title={titulo} /><Card><ErrorState message={(error as Error)?.message || 'Veículo não encontrado.'} /></Card></>

  const d = data?.depreciacao
  return (
    <>
      <PageHeader title={titulo} />

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white">{v.apelido}</h3>
          <p className="text-13 text-muted">{v.modelo.marca} {v.modelo.modelo} · {formatKm(v.kmAtual)}</p>
        </div>
        <FiltroPeriodo de={de} ate={ate} onChange={(a, b) => { setDe(a); setAte(b) }} />
      </div>

      {isLoading ? <Card><LoadingState /></Card> : isError || !data ? <Card><ErrorState message="Não foi possível carregar os insights." /></Card> : (
        <div className="grid items-start gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader title="Custo no período" icon="ri-wallet-3-line" actions={<span className="text-xs text-muted">{formatDate(data.de)} a {formatDate(data.ate)}</span>} />
            <CardBody className="space-y-4">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <p className="text-2xl font-semibold text-primary">{formatMoney(data.custoTotal)}</p>
                  <p className="text-xs text-muted">{numeroBr(data.meses, 1)} meses · {formatKm(data.kmRodado)} rodados</p>
                </div>
                <div className="text-right">
                  <p className="text-2xs uppercase text-muted">Média mensal</p>
                  <p className="text-lg font-semibold text-white">{formatMoney(data.custoMedioMensal)}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Kpi rotulo="Só combustível" valor={porKm(data.custoPorKm.combustivel)} />
                <Kpi rotulo="Operacional" valor={porKm(data.custoPorKm.operacional)} />
                <Kpi rotulo="Com depreciação" valor={porKm(data.custoPorKm.real)} />
              </div>
              {data.kmRodado <= 0 && <p className="text-xs text-muted">Sem km rodado no período: os custos por km ficam em branco. Registre abastecimentos ou o km para calcular.</p>}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Composição" icon="ri-pie-chart-line" />
            <CardBody className="space-y-3">
              {data.composicao.length === 0 ? <p className="text-13 text-muted">Sem custos no período.</p> : data.composicao.map((c) => (
                <div key={c.tipo} className="space-y-1">
                  <div className="flex justify-between text-13"><span className="text-white">{TIPO_CUSTO_VEICULO_LABEL[c.tipo]}</span><span className="text-muted">{numeroBr(c.percentual, 0)}% · {formatMoney(c.valor)}</span></div>
                  <ProgressBar value={c.percentual} />
                </div>
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Depreciação e custo real" icon="ri-line-chart-line" actions={d && <Badge variant="info" className="normal-case">Dados FIPE</Badge>} />
            <CardBody className="space-y-3">
              {!d ? <p className="text-13 text-muted">{data.depreciacaoAviso || 'Sem dados para calcular a depreciação deste veículo.'}</p> : (
                <>
                  <dl className="space-y-1.5 text-13">
                    <Linha rotulo={`Pago em ${formatDate(d.dataCompra)}`} valor={formatMoney(d.valorCompra)} />
                    <Linha rotulo={`Valor FIPE (ref. ${mesReferencia(d.mesReferenciaFipe)})`} valor={formatMoney(d.valorFipe)} />
                    <Linha rotulo={d.perda >= 0 ? 'Perda de valor' : 'Valorização'} valor={`${formatMoney(Math.abs(d.perda))} · ${numeroBr(Math.abs(d.percentual), 0)}%`} cor={d.perda > 0 ? 'text-expense' : 'text-income'} />
                  </dl>
                  <p className="text-13 text-muted">
                    Média de <span className="text-white">{formatMoney(d.mediaMensal)}</span> por mês em {d.mesesDesdeCompra} meses
                    {data.custoMedioMensal > 0 && <> . Somada ao gasto operacional ({formatMoney(data.custoMedioMensal)}/mês), o veículo custa em média <span className="font-medium text-primary">{formatMoney(data.custoMedioMensal + d.mediaMensal)}</span> por mês</>}.
                  </p>
                  <p className="text-xs text-muted">Não é avaliação oficial. Fonte: Tabela FIPE.</p>
                </>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Alertas" icon="ri-alarm-warning-line" />
            <CardBody className="space-y-2">
              {data.alertas.length === 0 ? <p className="text-13 text-muted">Nenhum alerta no período.</p> : data.alertas.map((a, i) => {
                const est = ALERTA[a.tipo] ?? ALERTA[TipoAlertaVeiculo.ConsumoPior]
                return (
                  <div key={i} className="flex items-start gap-3 rounded bg-surface2 px-3 py-2.5">
                    <span className={cn('flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full', est.tom)}><i className={est.icone} aria-hidden /></span>
                    <div className="min-w-0 text-13">
                      <p className="text-white">{a.mensagem}</p>
                      {a.data && <p className="text-2xs text-muted">{formatDate(a.data)}</p>}
                      {a.abastecimentoId && <Link to={`/veiculos/${v.id}/abastecimentos`} className="text-xs text-primary hover:underline">Revisar abastecimentos</Link>}
                    </div>
                  </div>
                )
              })}
            </CardBody>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader title="Projeção" icon="ri-funds-line" />
            <CardBody className="space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Kpi rotulo="Gasto mensal" valor={formatMoney(data.projecao.gastoMensalMedio)} />
                <Kpi rotulo="Projeção anual" valor={formatMoney(data.projecao.projecaoAnual)} />
                <Kpi rotulo="Anual + depreciação" valor={data.projecao.projecaoAnualComDepreciacao != null ? formatMoney(data.projecao.projecaoAnualComDepreciacao) : '—'} />
                <Kpi rotulo="Uso médio" valor={data.projecao.kmPorDia != null ? `${numeroBr(data.projecao.kmPorDia, 0)} km/dia` : '—'} />
              </div>
              <p className="text-xs text-muted">
                Baseada na média do período{data.projecao.kmPorDia != null ? `, em quilometragem constante de ${numeroBr(data.projecao.kmPorDia, 0)} km por dia` : ''}
                {data.projecao.kmAnoProjetado != null ? ` (cerca de ${formatKm(Math.round(data.projecao.kmAnoProjetado))} em um ano)` : ''}.
              </p>
            </CardBody>
          </Card>
        </div>
      )}

      <p className="mt-6 text-xs text-muted"><Link to={`/veiculos/${v.id}`} className="hover:text-primary"><i className="ri-arrow-left-line" aria-hidden /> Voltar para {v.apelido}</Link></p>
    </>
  )
}

function Kpi({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="rounded bg-surface2 p-3">
      <p className="text-2xs uppercase text-muted">{rotulo}</p>
      <p className="mt-1 text-15 font-semibold text-white">{valor}</p>
    </div>
  )
}

function Linha({ rotulo, valor, cor = 'text-white' }: { rotulo: string; valor: string; cor?: string }) {
  return <div className="flex justify-between gap-3"><dt className="text-muted">{rotulo}</dt><dd className={cn('text-right', cor)}>{valor}</dd></div>
}
